"use client";

import type { ColumnDef } from "@tanstack/react-table";

import { DonorDisplay } from "@/components/donor-display";
import { DonationTypeBadge } from "@/components/donation-type-badge";
import { formatMoney, formatTableDate } from "@/lib/format";
import type { Donations } from "@/types/models/Donations";

const moneyHeaderClass = "justify-end text-right";
const moneyCellClass =
  "w-full text-right tabular-nums whitespace-nowrap text-sm font-semibold text-foreground";

function newDonorLabel(donation: Donations) {
  const donor = donation.donor;
  const count = donor?.donationCount;
  if (!donor || count == null) {
    return { kind: "unknown" as const };
  }
  if (count === 1) {
    return { kind: "new" as const };
  }
  return { kind: "returning" as const };
}

export function getHomeLatestDonationsColumns(
  onPreloadDonor: (donorId: number) => void,
): ColumnDef<Donations, unknown>[] {
  return [
    {
      id: "donor",
      header: "Name",
      enableSorting: false,
      cell: ({ row }) => {
        const donation = row.original;
        const donorStatus = newDonorLabel(donation);
        return (
          <div className="flex min-w-0 items-center gap-2">
            {donation.offline ? (
              <span className="text-sm font-medium text-foreground">
                Offline
              </span>
            ) : (
              <DonorDisplay
                donorId={donation.donor?.id}
                firstName={donation.donor?.firstName}
                lastName={donation.donor?.lastName}
                onPreload={onPreloadDonor}
              />
            )}
            {donorStatus.kind === "new" ? (
              <span className="inline-flex shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:text-emerald-300">
                New
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      id: "type",
      header: "Type",
      enableSorting: false,
      cell: ({ row }) => <DonationTypeBadge donation={row.original} />,
    },
    {
      id: "amount",
      header: () => <span className={moneyHeaderClass}>Amount</span>,
      enableSorting: false,
      meta: { cellClassName: "text-right" },
      cell: ({ row }) => {
        const { grossAmount, gogiveAmount = 0 } = row.original;
        const net = (grossAmount - gogiveAmount) / 100;
        return <div className={moneyCellClass}>{formatMoney(net)}</div>;
      },
    },
    {
      id: "date",
      header: "Date",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm text-muted-foreground">
          {formatTableDate(row.original.createdAt)}
        </div>
      ),
    },
  ];
}
